import { useState } from "react";
import { Check, ChevronDown, Copy } from "lucide-react";
import PaymentLayout from "../layouts/PaymentLinkLayouts";
import METHOD_LOGO from "./MethodLogos";
import { getVirtualAccountInstructions } from "../utils/virtualAccountInstruction";
import useTranslation from "../hooks/useTranslation";

const VA_PLACEHOLDER_REGEX = /\[[^\]]*\]/g;

export default function PaymentInstructionVA({
  onCheckStatus,
  virtualAccount,
  merchantName,
  bankCode,
  onBack
}) {
  const { t, language } = useTranslation();
  const formatInstructionStep = (step, va) => step.replace(VA_PLACEHOLDER_REGEX, va || t("paymentInstructionVA.yourVaNumberFallback"));
  const [copied, setCopied] = useState(false);
  const [openChannel, setOpenChannel] = useState(0);
  const bankLogo = METHOD_LOGO[bankCode] || "/placeholder.svg";
  const normalizedBankCode = String(bankCode || "").replace(/_LINKQU$/, "");
  const channels = getVirtualAccountInstructions(language)[normalizedBankCode]?.channels || [];

  const handleCopy = async () => {
    if (!virtualAccount) return;

    try {
      await navigator.clipboard.writeText(virtualAccount);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <PaymentLayout unframedContent onBack={onBack}>
      <div className="rounded-2xl bg-white p-5 shadow-sm md:p-7">
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-6 md:px-7">
          <div className="flex flex-col items-center gap-6 md:flex-row md:items-center md:justify-between">
            <div className="order-2 w-full md:order-1">
              <div>
                <p className="text-sm font-normal text-gray-500">{t("paymentInstructionVA.vaNumber")}</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="break-all text-base font-semibold leading-5 text-gray-900">
                    {virtualAccount || "-"}
                  </span>
                  {virtualAccount && (
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="shrink-0 text-singaHijauPrimary transition hover:text-singaHijauPrimary/80"
                      aria-label="Copy virtual account number"
                    >
                      {copied ? (
                        <Check size={15} strokeWidth={2.4} aria-hidden />
                      ) : (
                        <Copy size={15} strokeWidth={2.4} aria-hidden />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm font-normal text-gray-500">{t("paymentInstructionVA.vaName")}</p>
                <p className="mt-1 text-base font-semibold leading-5 text-gray-900 break-all">{merchantName || "-"}</p>
              </div>
            </div>

            <div className="order-1 flex w-full justify-center md:order-2 md:w-auto md:justify-end">
              <img
                src={bankLogo}
                alt="Bank logo"
                className="max-h-10 max-w-[132px] object-contain md:max-h-11 md:max-w-[150px]"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {channels.map((channel, index) => {
            const isOpen = openChannel === index;
            return (
              <div key={channel.name} className="border-b border-gray-200 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenChannel(isOpen ? null : index)}
                  className="flex h-14 w-full items-center justify-between gap-4 px-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {channel.name} {t("paymentInstructionVA.instructionsSuffix")}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-gray-700 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    strokeWidth={2.5}
                    aria-hidden
                  />
                </button>
                {isOpen && (
                  <ol className="space-y-2 px-4 pb-5">
                    {channel.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="text-sm text-gray-600">
                        {stepIndex + 1}. {formatInstructionStep(step, virtualAccount)}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onCheckStatus}
          className="mt-4 flex h-12 w-full items-center justify-center rounded-xl border border-singaHijauPrimary bg-white text-sm font-semibold text-singaHijauPrimary transition hover:bg-green-50"
        >
          {t("common.checkPaymentStatus")}
        </button>
      </div>
    </PaymentLayout>
  );
}
